/**
 * AlertCardGrid (Story 27.2) — a clone of the clients ClientCardGrid. Same card
 * chrome (!rounded-[22px], 0.5px border, shadow-xs hover:shadow-sm, inner 0.4
 * divider, 3-dot DropdownMenu with DotsThreeVertical size 22) per the
 * design-system directive §7. Card body swapped to the alert read-model.
 */

import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import { DotsThreeVertical, PencilSimple, Trash } from "@phosphor-icons/react";
import type { Alert } from "./types";
import {
  platformDisplayName,
  platformLogoUrl,
  humanizeMetricKey,
  readableCondition,
  intervalBadge,
  lastTriggeredLabel,
  isRecentlyTriggered,
} from "./alertDisplay";

interface AlertCardGridProps {
  alerts: Alert[];
  canManage: boolean;
  onAlertClick?: (alert: Alert) => void;
  onEditClick?: (alert: Alert) => void;
  onDeleteClick?: (alert: Alert) => void;
  onToggleActive?: (alert: Alert) => void;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
}

export function AlertCardGrid({
  alerts,
  canManage,
  onAlertClick,
  onEditClick,
  onDeleteClick,
  onToggleActive,
  triggerRefs,
}: AlertCardGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {alerts.map((alert) => {
        const metricLabel = humanizeMetricKey(alert.metricKey);
        const logo = platformLogoUrl(alert.platform);
        const recent = isRecentlyTriggered(alert.lastTriggeredAt);
        const statusClasses = alert.isActive
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-neutral-100 border-neutral-200 text-neutral-500";
        return (
          <Card
            key={alert.id}
            className={`shadow-xs hover:shadow-sm transition-shadow ${onAlertClick ? "cursor-pointer" : ""} !rounded-[22px]`}
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => onAlertClick?.(alert)}
            role={onAlertClick ? "link" : undefined}
            tabIndex={onAlertClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onAlertClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onAlertClick(alert);
              }
            }}
          >
            <CardContent className="p-0">
              <div className="flex items-center gap-3 px-4 pt-4 pb-3">
                {logo ? (
                  <img
                    src={logo}
                    alt=""
                    className="w-7 h-7 rounded-full shrink-0 object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-muted shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[14px] font-medium truncate min-w-0">
                      {alert.name}
                    </h3>
                    {recent && (
                      <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[11px] rounded-full px-2 py-0.5 shrink-0">
                        Triggered
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-muted-foreground truncate">
                    {platformDisplayName(alert.platform)} · {alert.clientName}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium shrink-0 ${statusClasses}`}
                >
                  {alert.isActive ? "Active" : "Paused"}
                </span>
              </div>
              <div
                className="mx-4"
                style={{ borderTop: "0.5px solid hsl(var(--input) / 0.5)" }}
              />
              <div className="pt-3 pb-3 px-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-foreground font-medium">
                    {readableCondition(metricLabel, alert.condition, alert.threshold)}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-foreground/70">
                    {intervalBadge(alert.interval)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[13px] text-foreground -mt-1">
                  <span>Last triggered {lastTriggeredLabel(alert.lastTriggeredAt)}</span>
                  {canManage && (
                    <div
                      className="ml-auto flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {onToggleActive && (
                        <button
                          type="button"
                          onClick={() => onToggleActive(alert)}
                          className="text-[12px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1.5 py-1 rounded-lg"
                          aria-label={`${alert.isActive ? "Pause" : "Resume"} ${alert.name}`}
                        >
                          {alert.isActive ? "Pause" : "Resume"}
                        </button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            ref={(el) => {
                              triggerRefs.current[alert.id] = el;
                            }}
                            variant="ghost"
                            size="icon"
                            className="min-h-8 min-w-8 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                            aria-label={`Actions for ${alert.name}`}
                          >
                            <DotsThreeVertical size={22} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="rounded-xl p-2 shadow-md"
                          style={{ border: "0.5px solid var(--hairline)" }}
                        >
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onEditClick?.(alert)}
                          >
                            <PencilSimple size={14} className="mr-1" />
                            Edit Alert
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                            onClick={() => onDeleteClick?.(alert)}
                          >
                            <Trash size={14} className="mr-1" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
